import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link, Breadcrumbs as MuiBreadcrumbs, Skeleton, Typography, } from "@mui/material";
import { useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { UserContext } from "../auth/UserProvider";
import { routeConfig, } from "./routeConfig";
import { useBreadcrumbData } from "./useBreadcrumbData";
/**
 * Normalizes a path by removing trailing slashes (except for root).
 */
const normalizePath = (path) => {
    if (path === "/")
        return path;
    return path.replace(/\/+$/, "");
};
/**
 * Matches a URL path against a route pattern and extracts parameter values.
 * Returns null if no match, or an object with extracted params if matched.
 */
const matchRoutePattern = (pathname, pattern) => {
    const normalizedPath = normalizePath(pathname);
    const normalizedPattern = normalizePath(pattern);
    const pathParts = normalizedPath.split("/").filter(Boolean);
    const patternParts = normalizedPattern.split("/").filter(Boolean);
    if (pathParts.length !== patternParts.length) {
        return null;
    }
    const params = {};
    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const pathPart = pathParts[i];
        if (patternPart.startsWith(":")) {
            // This is a parameter
            const paramName = patternPart.slice(1);
            params[paramName] = pathPart;
        }
        else if (patternPart !== pathPart) {
            // Static segment doesn't match
            return null;
        }
    }
    return params;
};
/**
 * Finds the best matching route config for a given path.
 * Returns the config and extracted parameters.
 */
const findRouteConfig = (pathname) => {
    const normalizedPath = normalizePath(pathname);
    for (const pattern of Object.keys(routeConfig)) {
        const params = matchRoutePattern(normalizedPath, pattern);
        if (params !== null) {
            const config = routeConfig[pattern];
            if (config) {
                return { config, params };
            }
        }
    }
    return null;
};
/**
 * Parses the current URL into breadcrumb segments.
 * Builds up the path progressively to create the full breadcrumb trail.
 */
const parsePathSegments = (pathname) => {
    const normalizedPath = normalizePath(pathname);
    if (normalizedPath === "/") {
        return [
            {
                path: "/",
                fullPath: "/",
                label: "Home",
                isLast: true,
                isLinkable: true,
            },
        ];
    }
    const parts = normalizedPath.split("/").filter(Boolean);
    const segments = [];
    // Always start with Home
    segments.push({
        path: "/",
        fullPath: "/",
        label: "Home",
        isLast: false,
        isLinkable: true,
    });
    // Build up each segment
    let currentPath = "";
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        currentPath += "/" + part;
        const isLast = i === parts.length - 1;
        const match = findRouteConfig(currentPath);
        if (match) {
            segments.push({
                path: part,
                fullPath: currentPath,
                label: match.config.label,
                resolver: match.config.resolver,
                isLast,
                isLinkable: true,
            });
        }
        else {
            // No route config for this path - show label but don't make it a link
            // Format the segment name: "players" -> "Players", "external-users" -> "External Users"
            const formattedLabel = part
                .split("-")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
            segments.push({
                path: part,
                fullPath: currentPath,
                label: formattedLabel,
                isLast,
                isLinkable: false,
            });
        }
    }
    return segments;
};
/**
 * Extracts segments that need entity resolution for the data hook.
 */
const getResolverSegments = (segments) => {
    return segments
        .filter((s) => s.resolver)
        .map((s) => {
        // Extract the numeric ID from the path
        const pathParts = s.fullPath.split("/");
        const idPart = pathParts[pathParts.length - 1] ?? "";
        const id = parseInt(idPart, 10);
        // If the ID is not in the last position, find it
        if (isNaN(id)) {
            // Look for numeric segment in the path
            for (let i = pathParts.length - 1; i >= 0; i--) {
                const part = pathParts[i];
                if (part) {
                    const num = parseInt(part, 10);
                    if (!isNaN(num)) {
                        return { resolver: s.resolver, value: num };
                    }
                }
            }
        }
        return { resolver: s.resolver, value: id };
    })
        .filter((s) => !isNaN(s.value));
};
export const Breadcrumbs = () => {
    const { user } = useContext(UserContext);
    const location = useLocation();
    const segments = parsePathSegments(location.pathname);
    const resolverSegments = getResolverSegments(segments);
    const resolvedNames = useBreadcrumbData(resolverSegments);
    // Don't show breadcrumbs on home page or for unauthenticated users
    if (!user || location.pathname === "/" || segments.length <= 1) {
        return null;
    }
    const getDisplayLabel = (segment) => {
        if (segment.resolver) {
            const name = resolvedNames[segment.resolver];
            if (resolvedNames.isLoading && !name) {
                return _jsx(Skeleton, { width: 80 });
            }
            return name || segment.label;
        }
        return segment.label;
    };
    return (_jsx(MuiBreadcrumbs, { separator: _jsx(NavigateNextIcon, { fontSize: "small" }), "aria-label": "breadcrumb", maxItems: 5, itemsBeforeCollapse: 1, itemsAfterCollapse: 2, sx: { mb: 2 }, children: segments.map((segment) => {
            const label = getDisplayLabel(segment);
            const isHome = segment.fullPath === "/";
            // Last segment or non-linkable segments are rendered as text
            if (segment.isLast || !segment.isLinkable) {
                return (_jsx(Typography, { color: segment.isLast ? "text.primary" : "inherit", sx: {
                        display: "flex",
                        alignItems: "center",
                        maxWidth: { xs: 150, sm: 250, md: "none" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }, children: label }, segment.fullPath));
            }
            return (_jsxs(Link, { component: RouterLink, to: segment.fullPath, underline: "hover", color: "inherit", sx: {
                    display: "flex",
                    alignItems: "center",
                    maxWidth: { xs: 150, sm: 250, md: "none" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }, children: [isHome && _jsx(HomeIcon, { sx: { mr: 0.5 }, fontSize: "small" }), label] }, segment.fullPath));
        }) }));
};
// Export for testing and reuse
export { findRouteConfig, getResolverSegments, matchRoutePattern, normalizePath, parsePathSegments, };
